import { BoxComponent } from '../../../base/box/Box';
import Carousel from '../../../components/Carousel/Carousel';
import { ICON_PLACEMENT } from '../../../components/Carousel/carousel.types';
import { Image } from '../../SharedComponents';
import ImageLoader from '../../SharedComponents/ImageLoader/ImageLoader';
import Video from '../../SharedComponents/Video';
import { MediaMap } from '@zeus/shared';
import { ImageProps, VideoProps } from '../../types.ts/types';
import { LocalStyles } from '../types';

interface OverlayStyles {
  overlayColor?: string;
  overlayOpacity?: number;
  blurValue?: number;
}

interface HeroBackgroundProps {
  variant: string;
  video: VideoProps;
  image: ImageProps;
  carousel: {
    toggle: boolean;
    value: any[];
    type: 'carousel';
  };
  builderMode: boolean;
  MediaMap?: MediaMap;
  sectionHeightStyles: Record<string, unknown>;
  localStyles: LocalStyles;
  currentIndex: number;
  isAutoPlay: boolean;
  getCurrentIndex: (value: number) => void;
  getCurrentActiveState: (value: boolean) => void;
  overlayStyles?: OverlayStyles;
  ImageComponent?: any;
  altText?: string;
  onBackgroundClick?: () => void;
}

export const HeroBackground = ({
  variant,
  video,
  image,
  carousel,
  builderMode,
  MediaMap,
  sectionHeightStyles,
  localStyles,
  currentIndex,
  isAutoPlay,
  altText,
  getCurrentIndex,
  getCurrentActiveState,
  overlayStyles,
  ImageComponent,
  onBackgroundClick,
}: HeroBackgroundProps) => {
  // Container for all background media - absolutely positioned
  const backgroundContainerStyles = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  };

  const renderBackground = () => {
    switch (variant) {
      case 'VIDEO':
        return (
          video.toggle && (
            <Video
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover' as const,
                zIndex: 0,
              }}
              playsInline={true}
              autoPlay
              muted
              lazyLoadPlaceholder={false}
              loop
              videoUrl={video.value}
              mediaMap={MediaMap}
              placeholderUrl={video.placeholder}
            />
          )
        );
      case 'STILL_IMAGE':
        return (
          image.toggle &&
          (image.loading && builderMode ? (
            <BoxComponent
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                zIndex: 0,
              }}
            >
              <ImageLoader globalStyles={localStyles} />
            </BoxComponent>
          ) : (
            <Image
              imageUrl={image.croppedFileUrl || image.value}
              altText={image.altText}
              lazyLoad={false}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover' as const,
                zIndex: 0,
              }}
              MediaMap={MediaMap}
              builderMode={builderMode}
              ImageComponent={ImageComponent}
            />
          ))
        );
      case 'CAROUSEL':
        return (
          carousel.toggle && (
            <BoxComponent
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
              }}
            >
              <Carousel
                images={carousel.value}
                MediaMap={MediaMap}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover' as const,
                  zIndex: 0,
                }}
                isBuilderDesktopMode={true}
                iconPlacement={ICON_PLACEMENT.ON_IMAGE}
                autoScroll={!isAutoPlay}
                returnIndex={getCurrentIndex}
                overlayStyles={overlayStyles}
                initialIndex={currentIndex}
              />
            </BoxComponent>
          )
        );
      default:
        return null;
    }
  };

  return (
    <BoxComponent
      sx={backgroundContainerStyles}
      onClick={(e) => {
        if (builderMode && onBackgroundClick) {
          e.stopPropagation();
          onBackgroundClick();
        }
      }}
    >
      {renderBackground()}
    </BoxComponent>
  );
};
